import { useCallback, useEffect, useState } from 'react';
import {
  createSavedRoom,
  deleteSavedRoom,
  fetchRoomMembers,
  fetchSavedRooms,
  removeRoomMember,
  setRoomMember,
  updateSavedRoom,
} from './api';
import type { RoomMember, SavedRoom } from './api';
import type { Route } from './routes';

/**
 * Rooms that outlive a call — and who may read what was said in them.
 *
 * WHY THIS PAGE EXISTS. Two things were true at once and neither was tolerable
 * for long: there was one org-wide room in the operator's configuration, so
 * holding a recurring meeting about anything else meant a deploy; and anyone who
 * could sign in could read every transcript in the building. The first is a
 * nuisance. The second is the sharp edge — a person invited to talk about one
 * thing could read the record of every other thing.
 *
 * Those are the same feature. You cannot scope a transcript to a group of people
 * until there is something durable for the group to hang off, and an ad-hoc room
 * cannot be that because it stops existing when the last person leaves. So a
 * room you own is the unit, and its membership is the answer to "who gets the
 * transcript".
 *
 * WHAT MEMBERSHIP DOES AND DOES NOT DO. It decides who may READ THE RECORD. It
 * does not gate joining the call: a meeting link you can send somebody is the
 * thing people actually want, and an invite already scopes a guest to one room.
 * Conflating the two would mean every attendee needed an account before they
 * could be in a meeting, which is the product nobody asked for.
 */

function when(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/** Everyone who can sign in, or only the people listed — said in words. */
function visibilityLabel(v: SavedRoom['visibility']): string {
  return v === 'members'
    ? 'Only its members can read what was said here'
    : 'Anyone who can sign in can read what was said here';
}

function NewRoom({ onCreated }: { onCreated: (r: SavedRoom) => void }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [members, setMembers] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = () => {
    setBusy(true);
    setError(null);
    createSavedRoom(title.trim(), members ? 'members' : 'staff')
      .then((r) => {
        onCreated(r);
        setOpen(false);
        setTitle('');
        setMembers(false);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setBusy(false));
  };

  if (!open) {
    return (
      <section className="mx-room-card mx-start">
        <div className="mx-room-head">
          <h2>New room</h2>
        </div>
        <p className="mx-muted">
          A room of your own: it keeps its link between meetings, it is listed here rather than in
          the server&rsquo;s configuration, and you decide who can read what was said in it.
        </p>
        <div className="mx-room-actions">
          <button className="mx-button mx-button-inline" onClick={() => setOpen(true)}>
            New room
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="mx-room-card mx-start">
      <div className="mx-room-head">
        <h2>New room</h2>
      </div>
      <form
        className="mx-start-form"
        onSubmit={(e) => {
          e.preventDefault();
          create();
        }}
      >
        <label>
          What is it for?
          <input
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Design weekly"
            maxLength={120}
          />
        </label>
        <label className="mx-check">
          <input type="checkbox" checked={members} onChange={(e) => setMembers(e.target.checked)} />
          {/* Phrased as the RESTRICTION rather than as the default, because the
              default is what every room does today and a checkbox that reads
              "anyone can see this" invites nobody to think about it. */}
          Only its members can read what was said here
        </label>
        <div className="mx-room-actions">
          <button className="mx-button mx-button-inline" type="submit" disabled={busy || !title.trim()}>
            {busy ? 'Creating…' : 'Create'}
          </button>
          <button className="mx-invite-btn" type="button" onClick={() => setOpen(false)}>
            Cancel
          </button>
        </div>
      </form>
      {error && <p className="mx-error">{error}</p>}
    </section>
  );
}

/**
 * The member list, opened from a room.
 *
 * Only rendered for somebody who can administer the room. The server refuses
 * everyone else, and a panel that loads and then 403s on every button is worse
 * than one that is not offered.
 */
function Members({ room, onClose }: { room: SavedRoom; onClose: () => void }) {
  const [list, setList] = useState<RoomMember[] | null>(null);
  const [who, setWho] = useState('');
  const [asAdmin, setAsAdmin] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    fetchRoomMembers(room.name)
      .then(setList)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [room.name]);

  useEffect(load, [load]);

  const add = () => {
    const principal = who.trim();
    if (!principal) return;
    setBusy(true);
    setError(null);
    setRoomMember(room.name, principal, asAdmin ? 'admin' : 'member')
      .then(() => {
        setWho('');
        setAsAdmin(false);
        load();
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setBusy(false));
  };

  const drop = (principal: string) => {
    setError(null);
    removeRoomMember(room.name, principal)
      .then(load)
      // The server refuses to remove the last admin and the refusal names the
      // fix ("make somebody else an admin first"). Shown verbatim, because
      // rewording it here would be a second place to keep that rule.
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  };

  return (
    <div className="mx-members">
      <div className="mx-room-head">
        <h3>Who can read {room.title}</h3>
        <button className="mx-back" onClick={onClose}>
          Done
        </button>
      </div>

      {room.visibility === 'staff' && (
        <p className="mx-muted">
          This room is readable by anyone who can sign in, so this list currently only decides who
          can <b>administer</b> it. Switch it to members-only to make the list decide who can read
          it too.
        </p>
      )}

      {error && <p className="mx-error">{error}</p>}
      {!list ? (
        <p className="mx-muted">Loading…</p>
      ) : (
        <ul className="mx-member-list">
          {list.map((m) => (
            <li key={m.principal}>
              <span className="mx-member-who">{m.principal}</span>
              {m.role === 'admin' && <span className="mx-badge mx-badge-op">admin</span>}
              <span className="mx-spacer" />
              <span className="mx-muted">added {when(m.addedAt)}</span>
              <button className="mx-trk-erase" onClick={() => drop(m.principal)}>
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      <form
        className="mx-member-add"
        onSubmit={(e) => {
          e.preventDefault();
          add();
        }}
      >
        <input
          value={who}
          onChange={(e) => setWho(e.target.value)}
          placeholder="Their sign-in identity"
          aria-label="Who to add"
        />
        <label className="mx-check">
          <input type="checkbox" checked={asAdmin} onChange={(e) => setAsAdmin(e.target.checked)} />
          admin
        </label>
        <button className="mx-invite-btn" type="submit" disabled={busy || !who.trim()}>
          Add
        </button>
      </form>
      <p className="mx-muted">
        {/* Naming the format matters: this is an OIDC subject, not an email
            address, and getting it wrong adds somebody who does not exist with
            no error to say so. */}
        A room can have more than one admin, and it must keep at least one — so the last admin
        cannot be removed until somebody else is promoted.
      </p>
    </div>
  );
}

function RoomCard({
  room,
  onChanged,
  onGone,
}: {
  room: SavedRoom;
  onChanged: (r: SavedRoom) => void;
  onGone: () => void;
}) {
  const [showMembers, setShowMembers] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isAdmin = room.role === 'admin';

  const toggleVisibility = () => {
    const next = room.visibility === 'members' ? 'staff' : 'members';
    setError(null);
    updateSavedRoom(room.name, { visibility: next })
      .then(onChanged)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  };

  return (
    <section className="mx-room-card">
      <div className="mx-room-head">
        <h2>{room.title}</h2>
        <span className="mx-room-name">{room.name}</span>
        {room.visibility === 'members' && <span className="mx-pill">members only</span>}
        {isAdmin && <span className="mx-badge mx-badge-op">admin</span>}
      </div>

      <p className="mx-muted">
        {visibilityLabel(room.visibility)} · created {when(room.createdAt)}
      </p>

      {error && <p className="mx-error">{error}</p>}

      <div className="mx-room-actions">
        {isAdmin && (
          <>
            <button className="mx-invite-btn" onClick={() => setShowMembers((v) => !v)}>
              {showMembers ? 'Hide members' : 'Members'}
            </button>
            <button className="mx-invite-btn" onClick={toggleVisibility}>
              {room.visibility === 'members' ? 'Open to all staff' : 'Restrict to members'}
            </button>
            <button className="mx-invite-btn" onClick={() => setConfirming(true)}>
              Delete
            </button>
          </>
        )}
      </div>

      {confirming && (
        <div className="mx-erase-dialog">
          <h3>Delete {room.title}?</h3>
          <ul>
            {/* Said before the button, for the same reason the erase dialog
                says it: the thing people fear here is losing the recordings,
                and they are not what goes. */}
            <li>
              Its <b>recordings, transcripts and summaries are not touched</b> — they are files with
              signed manifests and they stay in the archive.
            </li>
            <li>The room, its membership and its summary prompt are forgotten.</li>
          </ul>
          <div className="mx-erase-actions">
            <button className="mx-invite-btn" onClick={() => setConfirming(false)}>
              Cancel
            </button>
            <button
              className="mx-button mx-button-inline danger"
              onClick={() =>
                deleteSavedRoom(room.name)
                  .then(onGone)
                  .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
              }
            >
              Delete the room
            </button>
          </div>
        </div>
      )}

      {showMembers && isAdmin && <Members room={room} onClose={() => setShowMembers(false)} />}
    </section>
  );
}

export function Rooms({ navigate }: { navigate: (r: Route) => void }) {
  const [rooms, setRooms] = useState<SavedRoom[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    fetchSavedRooms()
      .then(setRooms)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  useEffect(load, [load]);

  return (
    <>
      <NewRoom onCreated={load} />

      {error && <p className="mx-error">{error}</p>}
      {!rooms ? (
        <p className="mx-muted">Loading…</p>
      ) : rooms.length === 0 ? (
        <p className="mx-muted">
          No rooms yet. A room keeps its link between meetings and decides who can read what was
          said in it — which is the thing the standing rooms above cannot do.
        </p>
      ) : (
        rooms.map((r) => (
          <RoomCard key={r.name} room={r} onChanged={load} onGone={load} />
        ))
      )}

      <nav className="mx-dash-links">
        <button className="mx-back" onClick={() => navigate({ name: 'dashboard' })}>
          Start or join a meeting
        </button>
      </nav>
    </>
  );
}
